import { useId, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../../api';
import { useResource } from '../../hooks';
import { labelFor, staffActionLabel } from '../../labels';
import { channelLabel } from '../../components/distribution/labels';
import { relativeTime } from '../../components/project/projectHealth';
import { ConfirmDialog, EmptyState, ErrorNotice, Skeleton, StatusBadge } from '../../components/ui';
import { staffApi } from '../../staffApi';
import type { AddonInfo, AddonKey, StaffAuditEvent, StaffOrganizationDetail } from '../../staffApi';
import { Callout, Table, Textarea } from '../../components/primitives';
import { formatDate } from './StaffOrganizationsPage';
import { StaffLicencePanel } from './StaffLicencePanel';

/** A pending staff action awaiting its confirmation dialog. */
type PendingAction =
  | { kind: 'grant'; addon: AddonInfo }
  | { kind: 'remove'; addon: AddonInfo }
  | { kind: 'suspend' }
  | { kind: 'reactivate' };

function joinedSentence(detail: StaffOrganizationDetail): string {
  const org = detail.organization;
  const when = formatDate(org.created_at);
  if (org.signup_source === 'self_serve') return `Signed up on ${when}.`;
  if (org.signup_source === 'checkout') return `Bought Commerce Ops on the website on ${when}.`;
  return `Created on ${when}, before sign-up source was recorded.`;
}

/** eventNote pulls the human reason out of an audit entry's details. */
export function eventNote(event: StaffAuditEvent): string {
  const details = event.details ?? {};
  const note = details.reason ?? details.note;
  return typeof note === 'string' ? note : '';
}

function eventSubject(event: StaffAuditEvent, addons: AddonInfo[]): string {
  const plan = event.details?.plan;
  if (typeof plan === 'string') return `Plan ${plan}`;
  const addonKey = event.details?.addon;
  if (typeof addonKey !== 'string') return '';
  return addons.find((a) => a.key === addonKey)?.label ?? addonKey;
}

export function StaffOrganizationPage({ addons }: { addons: AddonInfo[] }) {
  const { organizationId = '' } = useParams();
  const loaded = useResource(() => staffApi.getOrganization(organizationId), [organizationId], organizationId);
  // A staff action answers with the updated organization; it replaces what
  // was loaded until the page moves to another organization.
  const [updated, setUpdated] = useState<{ id: string; detail: StaffOrganizationDetail; message: string } | null>(null);
  const current = updated?.id === organizationId ? updated : null;
  const detail = current ? current.detail : loaded.data;
  const done = current?.message ?? '';
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  const fieldId = useId();

  if (loaded.loading && !detail) return <OrganizationSkeleton />;
  if (loaded.error) {
    const missing = loaded.error instanceof ApiError && loaded.error.status === 404;
    return (
      <section className="staff-page">
        <Breadcrumb name="Organization" />
        {missing ? (
          <EmptyState title="No such organization" description="">
            <Link className="button button--secondary" to="/staff">
              Back to organizations
            </Link>
          </EmptyState>
        ) : (
          <ErrorNotice error={loaded.error} />
        )}
      </section>
    );
  }
  if (!detail) return null;

  const org = detail.organization;
  const suspended = Boolean(org.suspended_at);

  function open(action: PendingAction) {
    setPending(action);
    setText('');
    setActionError(null);
  }

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    setActionError(null);
    try {
      let result: StaffOrganizationDetail;
      let message: string;
      switch (pending.kind) {
        case 'grant':
          result = await staffApi.grantAddon(org.id, pending.addon.key, text);
          message = `${pending.addon.label} is now included.`;
          break;
        case 'remove':
          result = await staffApi.removeAddon(org.id, pending.addon.key, text);
          message = `${pending.addon.label} was removed.`;
          break;
        case 'suspend':
          result = await staffApi.suspend(org.id, text);
          message = `${org.name} is suspended.`;
          break;
        case 'reactivate':
          result = await staffApi.reactivate(org.id, text);
          message = `${org.name} can use Commerce Ops again.`;
          break;
      }
      setUpdated({ id: org.id, detail: result, message });
      setPending(null);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  const dialog = dialogCopy(pending, org.name);

  return (
    <section className="staff-page">
      <Breadcrumb name={org.name} />

      <div className="overview-header staff-detail__header">
        <div>
          <h1 className="overview-header__title staff-detail__title">
            {org.name}
            {suspended ? <StatusBadge kind="organizationAccess" value="suspended" /> : null}
          </h1>
          <p className="type-description">
            <code>{org.slug}</code> {joinedSentence(detail)}
          </p>
        </div>
        <div className="overview-header__actions">
          {suspended ? (
            <button className="button" type="button" onClick={() => open({ kind: 'reactivate' })}>
              Reactivate
            </button>
          ) : (
            <button className="button button--danger-quiet" type="button" onClick={() => open({ kind: 'suspend' })}>
              Suspend organization
            </button>
          )}
        </div>
      </div>

      <p className="visually-hidden" aria-live="polite">
        {done}
      </p>
      {done ? (
        <Callout tone="success" role="note" className="staff-detail__done">
          {done}
        </Callout>
      ) : null}

      {suspended ? (
        <div className="panel panel--critical staff-suspension">
          <strong>
            Suspended {formatDate(org.suspended_at)}
            {detail.suspended_by ? ` by ${detail.suspended_by}` : ''}
          </strong>
          <p className="panel__note">Reason: {detail.suspended_reason || 'none recorded'}</p>
          <p className="panel__note muted">
            Members can sign in but cannot use anything. Their servers, backups, monitoring and product feeds keep
            running.
          </p>
        </div>
      ) : null}

      <div className="project-grid">
        <div className="project-grid__col project-grid__col--8">
          <section className="panel" aria-labelledby={`${fieldId}-addons`}>
            <h2 id={`${fieldId}-addons`} className="staff-panel__title">
              Add-ons
            </h2>
            <Table label="Add-ons" compact className="staff-addons">
              <thead>
                <tr>
                  <th scope="col" className="staff-addons__name">Add-on</th>
                  <th scope="col" className="staff-addons__included">Included</th>
                  <th scope="col" className="staff-addons__use">In use</th>
                  <th scope="col">
                    <span className="visually-hidden">Change</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {addons.map((addon) => {
                  const grant = detail.addons.find((a) => a.addon === addon.key);
                  const inUse = org.addons_in_use.includes(addon.key as AddonKey);
                  return (
                    <tr key={addon.key}>
                      <td>
                        <div className="staff-table__name">{addon.label}</div>
                        <div className="staff-table__sub">In use means: {addon.in_use_means}</div>
                      </td>
                      <td>
                        {grant ? (
                          <>
                            <StatusBadge kind="addon" value="included" />
                            <div className="staff-table__sub">
                              {formatDate(grant.granted_at)}: {grant.note}
                            </div>
                          </>
                        ) : (
                          <span className="muted">Not included</span>
                        )}
                      </td>
                      <td>
                        {inUse ? (
                          <StatusBadge kind="addon" value={grant ? 'in_use' : 'in_use_not_included'} />
                        ) : (
                          <span className="muted">No</span>
                        )}
                      </td>
                      <td className="staff-addons__action">
                        {grant ? (
                          <button
                            className="button button--secondary button--small"
                            type="button"
                            onClick={() => open({ kind: 'remove', addon })}
                            aria-label={`Remove ${addon.label}`}
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            className="button button--small"
                            type="button"
                            onClick={() => open({ kind: 'grant', addon })}
                            aria-label={`Include ${addon.label}`}
                          >
                            Include
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <p className="panel__note muted">
              Nothing is switched off yet when an add-on is not included; this records what the organization pays for.
            </p>
          </section>

          <StaffLicencePanel
            organizationId={org.id}
            onChanged={(message) => {
              // Reload so staff activity shows the change.
              setUpdated({ id: org.id, detail, message });
              staffApi
                .getOrganization(org.id)
                .then((fresh) => setUpdated({ id: org.id, detail: fresh, message }))
                .catch(() => undefined);
            }}
          />

          <section className="panel" aria-labelledby={`${fieldId}-members`}>
            <h2 id={`${fieldId}-members`} className="staff-panel__title">
              Members <span className="muted">{detail.members.length}</span>
            </h2>
            <Table label="Members" compact>
              <thead>
                <tr>
                  <th scope="col">Member</th>
                  <th scope="col">Role</th>
                  <th scope="col">Joined</th>
                  <th scope="col">Last sign-in</th>
                </tr>
              </thead>
              <tbody>
                {detail.members.map((member) => (
                  <tr key={member.user_id}>
                    <td>
                      <div>{member.name}</div>
                      <div className="staff-table__sub">
                        {member.email}
                        {member.email_verified ? null : <StatusBadge kind="emailVerification" value="not_verified" label="Email not verified" />}
                      </div>
                    </td>
                    <td>{member.is_owner ? 'Owner' : member.role_name}</td>
                    <td>{formatDate(member.joined_at)}</td>
                    <td>
                      {member.last_sign_in_at ? (
                        <time dateTime={member.last_sign_in_at} title={new Date(member.last_sign_in_at).toLocaleString()}>
                          {relativeTime(member.last_sign_in_at)}
                        </time>
                      ) : (
                        <span className="muted">Not recorded</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </section>

          <section className="panel" aria-labelledby={`${fieldId}-activity`}>
            <h2 id={`${fieldId}-activity`} className="staff-panel__title">
              Staff activity
            </h2>
            {detail.activity.length === 0 ? (
              <p className="panel__note muted">No activity</p>
            ) : (
              <ol className="staff-log">
                {detail.activity.map((event) => {
                  const subject = eventSubject(event, addons);
                  const note = eventNote(event);
                  return (
                    <li key={event.id}>
                      <div className="staff-log__line">
                        <strong>{labelFor(staffActionLabel, event.action)}</strong>
                        {subject ? <span>{subject}</span> : null}
                        <span className="muted">by {event.actor}</span>
                        <time className="muted staff-log__when" dateTime={event.occurred_at} title={new Date(event.occurred_at).toLocaleString()}>
                          {relativeTime(event.occurred_at)}
                        </time>
                      </div>
                      {note ? <p className="staff-log__note">{note}</p> : null}
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </div>

        <div className="project-grid__col project-grid__col--4">
          <section className="panel" aria-labelledby={`${fieldId}-subscription`}>
            <h2 id={`${fieldId}-subscription`} className="staff-panel__title">
              Subscription
            </h2>
            <div>
              <StatusBadge kind="subscription" value={org.subscription_status} />
            </div>
            <dl className="staff-facts">
              <div>
                <dt>Period ends</dt>
                <dd>{formatDate(org.subscription_current_period_end)}</dd>
              </div>
              <div>
                <dt>Stripe customer</dt>
                <dd>{detail.stripe_customer_id ? <code>{detail.stripe_customer_id}</code> : <span className="muted">None</span>}</dd>
              </div>
              <div>
                <dt>Stripe subscription</dt>
                <dd>
                  {detail.stripe_subscription_id ? <code>{detail.stripe_subscription_id}</code> : <span className="muted">None</span>}
                </dd>
              </div>
            </dl>
          </section>

          <section className="panel" aria-labelledby={`${fieldId}-usage`}>
            <h2 id={`${fieldId}-usage`} className="staff-panel__title">
              Usage
            </h2>
            <dl className="staff-facts staff-facts--numbers">
              <div>
                <dt>Projects</dt>
                <dd>{detail.usage.projects}</dd>
              </div>
              <div>
                <dt>Environments</dt>
                <dd>{detail.usage.environments}</dd>
              </div>
              <div>
                <dt>Servers online</dt>
                <dd>
                  {detail.usage.servers_online} of {detail.usage.servers}
                </dd>
              </div>
              <div>
                <dt>Deployments, last 30 days</dt>
                <dd>{detail.usage.deployments_30_days}</dd>
              </div>
              <div>
                <dt>Magento websites</dt>
                <dd>{detail.usage.magento_websites}</dd>
              </div>
            </dl>
            <h3 className="staff-panel__subtitle">Product distribution channels</h3>
            {detail.usage.distribution_channels.length === 0 ? (
              <p className="panel__note muted">None</p>
            ) : (
              <dl className="staff-facts staff-facts--numbers">
                {detail.usage.distribution_channels.map((channel) => (
                  <div key={channel.channel_type}>
                    <dt>{labelFor(channelLabel, channel.channel_type)}</dt>
                    <dd>
                      {channel.enabled} of {channel.total} enabled
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={dialog.title}
        description={dialog.description}
        confirmLabel={dialog.confirmLabel}
        danger={pending?.kind === 'suspend' || pending?.kind === 'remove'}
        busy={busy}
        confirmDisabled={text.trim() === ''}
        onConfirm={confirm}
        onCancel={() => {
          if (!busy) setPending(null);
        }}
      >
        <div className="field staff-dialog__field">
          <label className="field__label" htmlFor={`${fieldId}-text`}>
            {dialog.fieldLabel}
          </label>
          <Textarea
            id={`${fieldId}-text`}
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={3}
            maxLength={500}
            required
            autoFocus
            disabled={busy}
            aria-describedby={`${fieldId}-hint`}
          />
          <span className="field__hint" id={`${fieldId}-hint`}>
            {dialog.fieldHint}
          </span>
        </div>
        <ErrorNotice error={actionError} />
      </ConfirmDialog>
    </section>
  );
}

function dialogCopy(pending: PendingAction | null, name: string) {
  switch (pending?.kind) {
    case 'grant':
      return {
        title: `Include ${pending.addon.label}`,
        description: `${name} will be recorded as paying for ${pending.addon.label}. Nothing in their app changes yet.`,
        confirmLabel: 'Include add-on',
        fieldLabel: 'Why is it included?',
        fieldHint: 'For example the order, invoice or agreement. Shown in staff activity.',
      };
    case 'remove':
      return {
        title: `Remove ${pending.addon.label}`,
        description: `${name} will no longer be recorded as paying for ${pending.addon.label}. Nothing in their app changes yet.`,
        confirmLabel: 'Remove add-on',
        fieldLabel: 'Why is it removed?',
        fieldHint: 'Shown in staff activity.',
      };
    case 'reactivate':
      return {
        title: `Reactivate ${name}`,
        description: 'Its members can use Commerce Ops again straight away.',
        confirmLabel: 'Reactivate',
        fieldLabel: 'Why is it reactivated?',
        fieldHint: 'Shown in staff activity.',
      };
    case 'suspend':
    default:
      return {
        title: `Suspend ${name}`,
        description:
          'Its members can still sign in, but every page tells them the organization is suspended and nothing else works. Servers, backups, monitoring and product feeds keep running. Its store extensions keep working for the licence grace period, then stop sending data and applying ERP updates. You can reactivate it at any time.',
        confirmLabel: 'Suspend organization',
        fieldLabel: 'Reason',
        fieldHint: 'Staff only; members never see it.',
      };
  }
}

function Breadcrumb({ name }: { name: string }) {
  return (
    <nav className="staff-breadcrumb" aria-label="Breadcrumb">
      <ol>
        <li>
          <Link to="/staff">Organizations</Link>
        </li>
        <li aria-current="page">{name}</li>
      </ol>
    </nav>
  );
}

function OrganizationSkeleton() {
  return (
    <section className="staff-page" role="status" aria-label="Loading organization">
      <Skeleton width="160px" height="14px" />
      <div className="staff-detail__header">
        <Skeleton width="280px" height="28px" />
      </div>
      <div className="project-grid staff-detail__grid">
        <div className="project-grid__col project-grid__col--8">
          <Skeleton width="100%" height="220px" radius="var(--radius-md)" />
          <Skeleton width="100%" height="160px" radius="var(--radius-md)" />
        </div>
        <div className="project-grid__col project-grid__col--4">
          <Skeleton width="100%" height="170px" radius="var(--radius-md)" />
          <Skeleton width="100%" height="240px" radius="var(--radius-md)" />
        </div>
      </div>
    </section>
  );
}
